import React, { useEffect } from "react";
import {
    ButtonProps,
    ButtonRenderProps,
    ClearFiltersButton,
    ContentSection,
    DateRangePicker,
    DropdownMultiselect,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    SearchBox,
    Stack
} from "sprint-common-web";

import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { EnvironmentFeatureToggler } from "../../../core/environment-access/EnvironmentFeatureToggler";
import { Environment } from "../../../core/types/Environment";
import { ciRoutes } from "../../ContentIngestionRoutes";
import { ApproveCloudFileNotificationsButton } from "./buttons/approve-notifications/ApproveCloudFileNotificationsButton";
import { ClearCloudFilesButton } from "./buttons/clear/ClearCloudFilesButton";
import { RejectMultipleCloudFilesButton } from "./buttons/reject/reject-multiple/RejectMultipleCloudFilesButton";
import { RequestMultipleCloudFilesWaitingForClientResponseButton } from "./buttons/request-waiting-for-client-response/multiple/RequestMultipleCloudFilesWaitingForClientResponseButton";
import { createCloudFileListingColumns } from "./createCloudFileListingColumns";
import { useCloudFileListing } from "./hooks/useCloudFileListing";
import { useCloudFileListingFilters } from "./hooks/useCloudFileListingFilters";

export const CloudFileListing: React.VFC = () => {
    const { items, totalItems, isLoading, getData, setSelectedItems, selectedItems } = useCloudFileListing();

    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setFileName,
        setDeliveryRef,
        setArrivalDateStart,
        setArrivalDateEnd,
        setStatuses,
        statusOptions,
        clearFilters
    } = useCloudFileListingFilters();

    useEffect(() => {
        getData(filters);
    }, [filters]);

    const columns = createCloudFileListingColumns();

    const rejectCloudFilesButton: ButtonRenderProps<ButtonProps> = {
        render: RejectMultipleCloudFilesButton
    };

    const requestWaitingForClientResponseButton: ButtonRenderProps<ButtonProps> = {
        render: RequestMultipleCloudFilesWaitingForClientResponseButton
    };

    const approveCloudFileNotificationsButton: ButtonRenderProps<ButtonProps> = {
        render: ApproveCloudFileNotificationsButton
    };

    const clearCloudFilesButton: ButtonRenderProps<ButtonProps> = {
        render: () => (
            <EnvironmentFeatureToggler excludedEnvironments={[Environment.Prod]}>
                <ClearCloudFilesButton />
            </EnvironmentFeatureToggler>
        )
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={ciRoutes} />
            </PageHeader>
            <ContentSection
                header="Ingest"
                buttons={[
                    requestWaitingForClientResponseButton,
                    rejectCloudFilesButton,
                    approveCloudFileNotificationsButton,
                    clearCloudFilesButton
                ]}
            >
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="File name"
                                onChange={setFileName}
                                value={filters.fileName}
                                testId="cloud-files-filters-name"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                placeholder="Reference"
                                onChange={setDeliveryRef}
                                value={filters.deliveryRef}
                                testId="cloud-files-filters-reference"
                            />
                        </GridColumn>
                        <GridColumn sm={4}>
                            <DateRangePicker
                                onDateStartSelect={setArrivalDateStart}
                                onDateEndSelect={setArrivalDateEnd}
                                startValue={filters.arrivalDateStart ? new Date(filters.arrivalDateStart) : undefined}
                                endValue={filters.arrivalDateEnd ? new Date(filters.arrivalDateEnd) : undefined}
                                testId="cloud-files-filters-date-range"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Status"
                                selectedKeys={filters.statuses}
                                onChange={setStatuses}
                                options={statusOptions}
                                testId="cloud-files-filters-status"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton isLabeled={true} onClear={clearFilters} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                selectedItems={selectedItems}
                                items={items}
                                pagination={{
                                    page: filters.page,
                                    pageSize: filters.pageSize,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                onSelectionChange={setSelectedItems}
                                isLoading={isLoading}
                                testId="cloud-files-listing-table"
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};
